import { NO_DATA, TOKEN_SYMBOL } from '@/app/constants';
import { Pill, type PillType } from '@frekuent/orbit/components';
import i18n, {
	refundTypeTradPrefix,
	transactionStatusTradPrefix,
	transactionTypesTradPrefix,
} from '@/legacy/i18n';
import { RefundType, TransactionStatus, TransactionType } from '@/legacy/types';
import { formatDateForDisplay, formatTimeForDisplay, fromISODateTimeLocal } from '@/legacy/utils';
import { capitalizeFirstLetter } from '@/legacy/utils/language';
import { TFunction } from 'i18next';
import React from 'react';
import { otherTableTradPrefix } from '../constants';
import { CurrencyMeta, formatCurrency } from '@/shared/currency';
import { getLanguage } from '@/legacy/utils/language';

export function renderMoneyCell(
	value: number | undefined,
	token: number | undefined,
	prefix: string | null | undefined,
	meta: CurrencyMeta,
) {
	if (value === null || value === undefined) {
		return '-';
	}
	const formatted = formatCurrency(value, meta, getLanguage());
	return React.createElement(
		'div',
		{
			className: `flex items-baseline  ${token && 'gap-2'}`,
		},
		[
			React.createElement(
				'div',
				{
					className: 'flex gap-1',
				},
				[
					prefix ? React.createElement('span', { key: 'prefix' }, prefix) : null,
					React.createElement('span', { key: 'amount' }, formatted),
				],
			),
			token
				? React.createElement(
						'span',
						{
							className: 'block   border-l border-secondary h-4 self-center',
						},
						null,
					)
				: null,

			token
				? React.createElement('span', { className: 'text-sm flex' }, `${token} ${TOKEN_SYMBOL}`)
				: null,
		],
	);
}

export function renderDateAndHour(value: string) {
	const date = fromISODateTimeLocal(value);

	if (isNaN(date.getTime())) {
		return '-';
	}

	const formattedDate = formatDateForDisplay(date);
	const formattedTime = formatTimeForDisplay(date);

	return React.createElement(
		'div',
		{
			className: 'flex flex-col gap-1',
		},
		[
			React.createElement(
				'span',
				{
					className: 'text-sm block',
				},
				formattedDate,
			),
			React.createElement(
				'span',
				{
					className: 'text-xs block',
				},
				formattedTime,
			),
		],
	);
}

export const renderDate = (value: string) => {
	const date = fromISODateTimeLocal(value);

	if (isNaN(date.getTime())) {
		return '-';
	}

	const formattedDate = formatDateForDisplay(date);

	return React.createElement(
		'span',
		{
			className: 'text-sm block',
		},
		formattedDate,
	);
};

export function renderValueWithSubvalue(
	value: string,
	subvalue: string,
	valueClassName?: string,
	subvalueClassName?: string,
) {
	if (value === null || value === undefined) {
		value = '-';
	}

	if (subvalue === null || subvalue === undefined) {
		return React.createElement(
			'span',
			{
				className: 'text-sm block' + (valueClassName ?? ''),
			},
			value,
		);
	}
	return React.createElement(
		'div',
		{
			className: 'flex flex-col gap-1',
		},
		[
			React.createElement(
				'span',
				{
					className: 'text-sm block' + (valueClassName ?? ''),
				},
				value,
			),
			React.createElement(
				'span',
				{
					className: 'text-xs block' + (subvalueClassName ?? ''),
				},
				subvalue,
			),
		],
	);
}

// Shown as a red "completed" pill rather than their own label — the sale itself completed,
// only the (manual/auto) refund attempt on it failed.
const COMPLETED_LABEL_STATUSES: TransactionStatus[] = ['manual_fail', 'auto_fail'];

export const renderTransactionStatus = (value: TransactionStatus | undefined) => {
	if (!value) {
		return '-';
	}

	const badgeMapping: Record<TransactionStatus, PillType> = {
		refund: 'default',
		canceled: 'error',
		pending: 'default',
		completed: 'success',
		denied: 'error',
		partial_refund: 'default',
		in_process: 'warning',
		refund_denied: 'error',
		confirmed: 'success',
		refund_pending: 'warning',
		delivery_failed: 'error',
		payment_failed: 'error',
		timeout: 'error',
		expired: 'default',
		failed: 'error',
		error: 'error',
		manual_fail: 'error',
		auto_fail: 'error',
	};

	const labelStatus = COMPLETED_LABEL_STATUSES.includes(value) ? 'completed' : value.toLowerCase();

	return React.createElement(Pill, {
		label: i18n.t(`${transactionStatusTradPrefix}.${labelStatus}`),
		pillType: badgeMapping[value.toLowerCase() as TransactionStatus],
	});
};

const FAILED_REFUND_TYPES: RefundType[] = ['auto_fail', 'manual_fail'];

export const renderRefundType = (value: RefundType | null | undefined) => {
	if (!value) {
		return '-';
	}

	return React.createElement(Pill, {
		label: i18n.t(`${refundTypeTradPrefix}.${value.toLowerCase()}`),
		pillType: FAILED_REFUND_TYPES.includes(value) ? 'error' : 'info',
	});
};

export const renderCell = (value: any, renderFunction?: (value: any) => React.ReactNode) => {
	if (value === null || value === undefined || '' === value) {
		return NO_DATA;
	}

	if (renderFunction) {
		return renderFunction(value);
	}

	return value;
};

export const renderCardNumber = (cardNumber: string | null | undefined) => {
	// The backend builds some activity rows with pandas and stringifies an empty card as `"nan"`
	// (`str(NaN)` in table_machine_activity.normalize_bin_last4). Drop once the backend sends null.
	if (!cardNumber || cardNumber === 'nan') return NO_DATA;
	const bin = cardNumber.slice(0, -4);
	return `${bin ? `${bin} ` : ''}****${cardNumber.slice(-4)}`;
};

export const renderTransactionTypeCell = (value: TransactionType, t: TFunction) => {
	return React.createElement(
		'span',
		{ className: 'font-medium' },
		capitalizeFirstLetter(t(`${transactionTypesTradPrefix}.${value.toLowerCase()}`)),
	);
};

// Only 'Ok' is reconciled; every other backend status (Ko, No provider/frekuent transaction) reads as unreconciled.
export const renderConciliationStatusBadge = (
	status: string | null,
	t: TFunction,
	unreconciledCount?: number | null,
) => {
	const isReconciled = status === 'Ok';
	const unreconciledLabel =
		unreconciledCount == null
			? t('conciliation_status.unreconciled')
			: `${t('conciliation_status.unreconciled')} (${unreconciledCount})`;
	return React.createElement(Pill, {
		pillType: isReconciled ? 'success' : 'warning',
		label: isReconciled ? t('conciliation_status.reconciled') : unreconciledLabel,
	});
};

export const renderActiveDays = (active_time: number, t: TFunction): string => {
	if (active_time === 0) {
		return t(`${otherTableTradPrefix}.no_active_time`);
	}
	if (active_time === 1) {
		return `${active_time} ${t(`${otherTableTradPrefix}.day`)}`;
	}
	if (active_time > 1) {
		return `${active_time} ${t(`${otherTableTradPrefix}.days`)}`;
	}
	if (active_time < 0) {
		return '-';
	}
	return '-';
};
